import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { get, post } from '../../api/apiClient';
import { Eye, Search, X, CheckCircle, XCircle, AlertCircle, Clock, Plus, Home } from 'lucide-react';
import PaginationControls from '../../components/common/Pagination/PaginationControls';
import WfhDetailsModal from '../../components/common/WfhDetailsModal';
import WfhApplyModal from '../../components/common/WfhApplyModal';
import { formatDate as stringUtilsFormatDate } from '../../utils/stringUtils';

const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  return stringUtilsFormatDate(dateValue, { includeTime: false });
};

const getEmployeeName = (item) => {
  if (!item?.employeeId) return 'Employee';
  if (item.employeeId.personalInfo) {
    return `${item.employeeId.personalInfo.firstName || ''} ${item.employeeId.personalInfo.lastName || ''}`.trim() || 'Employee';
  }
  return 'Employee';
};

const MyWFHRequests = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal States
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Query: My WFH Requests
  const { data: wfhData, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['my-wfh-requests', statusFilter, currentPage, itemsPerPage],
    queryFn: async () => {
      const response = await get(`/work-from-home/my?page=${currentPage}&limit=${itemsPerPage}&status=${statusFilter}`);
      if (response?.success || response?.responseCode === 200) {
        return response?.data || { requests: [], pagination: {} };
      }
      throw new Error('Failed to load WFH requests.');
    }
  });

  const requests = wfhData?.requests || [];
  const pagination = wfhData?.pagination || { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: itemsPerPage };

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const error = queryError?.message || '';

  // Mutation: Apply WFH (self)
  const applyMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await post('/work-from-home', payload);
      if (!response?.success && response?.responseCode !== 201 && response?.responseCode !== 200) {
        throw new Error(response?.message || response?.error || 'Failed to submit WFH request.');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('WFH request submitted successfully');
      setShowApplyModal(false);
      queryClient.invalidateQueries({ queryKey: ['my-wfh-requests'] });
    },
    onError: (err) => {
      const errMsg = err.response?.data?.message || err.response?.data?.error || err.responseData?.message || err.responseData?.error || err.message || 'Failed to submit WFH request.';
      toast.error(errMsg);
    }
  });

  const applySubmitting = applyMutation.isPending;

  // Filter requests locally by Request ID (since search input is for Request ID/details)
  const filteredRequests = useMemo(() => {
    if (!searchTerm.trim()) return requests;
    const lower = searchTerm.toLowerCase();
    return requests.filter(r =>
      (r.requestId || '').toLowerCase().includes(lower) ||
      (r.reason || '').toLowerCase().includes(lower) ||
      (r.workLocation || '').toLowerCase().includes(lower)
    );
  }, [requests, searchTerm]);

  const openDetails = (request) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setSelectedRequest(null);
    setShowDetails(false);
  };

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || localStorage.getItem('user_data') || '{}');
    } catch {
      return {};
    }
  }, []);

  const userRole = (storedUser?.role || '').toLowerCase();
  const userDesignation = (storedUser?.designation || storedUser?.position || '').toLowerCase();
  const isTraineeOrIntern = userRole === 'trainee' || userRole === 'intern' || userRole.includes('trainee') || userRole.includes('intern') || userDesignation.includes('trainee') || userDesignation.includes('intern');

  return (
    <div className="flex flex-col gap-6 p-6 max-w-[1400px] mx-auto min-h-screen font-sans">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#0F2942] text-white flex items-center justify-center shadow-md">
            <Home className="w-6 h-6 stroke-[2]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0F2942] mb-0.5 leading-tight tracking-tight">My Work From Home Requests</h1>
            <p className="text-xs text-slate-500 font-medium">View and manage your self-applied WFH requests</p>
          </div>
        </div>
        {!isTraineeOrIntern && (
          <button
            onClick={() => setShowApplyModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F2942] text-white rounded-xl text-xs font-bold hover:bg-[#0a1a2a] transition-all shadow-sm active:scale-95 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Apply WFH
          </button>
        )}
      </div>

      {isTraineeOrIntern && (
        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200/80 text-amber-800 text-xs font-medium flex items-center gap-2.5 shadow-2xs">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
          <span>Work From Home applications are not applicable for Trainees and Interns as per company policy.</span>
        </div>
      )}

      {/* Toolbar / Filters (SaaS Toolbar Card) */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4">
        <div className="flex flex-wrap items-center gap-4 w-full">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by Request ID, location or reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.slice(0, 100))}
              className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs font-medium bg-slate-50/50 focus:bg-white transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs font-semibold bg-white cursor-pointer transition-all text-slate-700 shadow-2xs"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Clear Filters */}
          {(searchTerm || statusFilter !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-100 transition-all shadow-2xs bg-white"
            >
              <X className="h-3.5 w-3.5" />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto border border-slate-200/80 rounded-2xl bg-white shadow-sm overflow-hidden">
        <table className="w-full text-xs text-left">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200/80 text-slate-500">
              <th className="px-5 py-3.5 font-bold uppercase tracking-wider min-w-[120px]">Request ID</th>
              <th className="px-5 py-3.5 font-bold uppercase tracking-wider min-w-[110px]">From Date</th>
              <th className="px-5 py-3.5 font-bold uppercase tracking-wider min-w-[110px]">To Date</th>
              <th className="px-5 py-3.5 font-bold uppercase tracking-wider">Days</th>
              <th className="px-5 py-3.5 font-bold uppercase tracking-wider min-w-[130px]">Approved By</th>
              <th className="px-5 py-3.5 font-bold uppercase tracking-wider min-w-[100px]">Status</th>
              <th className="px-5 py-3.5 font-bold uppercase tracking-wider min-w-[80px]">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942]"></div>
                    <span className="text-slate-500 text-xs font-semibold">Loading requests...</span>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-rose-500 text-xs font-semibold">{error}</td>
              </tr>
            ) : filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 mb-4 shadow-2xs">
                      <Home className="w-7 h-7" />
                    </div>
                    <h3 className="text-base font-bold text-slate-800 mb-1">No WFH Requests Found</h3>
                    <p className="text-xs text-slate-500 mb-5 leading-relaxed">
                      You haven't submitted any work from home requests yet. Click below to create a new application.
                    </p>
                    <button
                      onClick={() => setShowApplyModal(true)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F2942] text-white rounded-xl text-xs font-bold hover:bg-[#0a1a2a] transition-all shadow-2xs"
                    >
                      <Plus className="w-4 h-4" /> Apply WFH
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredRequests.map((item) => {
                const approvedBy = [];
                if (item?.approvals?.status === 'approved') {
                  const approverName = item.approvals.actedBy?.firstName
                    ? `${item.approvals.actedBy.firstName} ${item.approvals.actedBy.lastName || ''}`.trim()
                    : 'System';
                  approvedBy.push(approverName);
                }
                return (
                  <tr key={item._id} className="hover:bg-blue-50/20 transition-colors">
                    <td className="px-5 py-4 whitespace-nowrap text-xs text-slate-800 font-mono font-bold">{item.requestId}</td>
                    <td className="px-5 py-4 whitespace-nowrap text-xs font-semibold text-slate-600">{formatDate(item.fromDate)}</td>
                    <td className="px-5 py-4 whitespace-nowrap text-xs font-semibold text-slate-600">{formatDate(item.toDate)}</td>
                    <td className="px-5 py-4 whitespace-nowrap text-xs font-bold text-slate-800">{item.totalDays}</td>
                    <td className="px-5 py-4 whitespace-nowrap text-xs font-semibold text-slate-600">{approvedBy.length ? approvedBy.join(', ') : '-'}</td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1.5">
                        {item.status === 'approved' && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                        {item.status === 'rejected' && <XCircle className="h-4 w-4 text-rose-500" />}
                        {item.status === 'pending' && <AlertCircle className="h-4 w-4 text-amber-500" />}
                        <span className={`text-xs px-2.5 py-1 rounded-full capitalize font-bold ${
                          item.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          item.status === 'rejected' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                          'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-xs font-semibold">
                      <button
                        onClick={() => openDetails(item)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all duration-200"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {/* Pagination */}
        <PaginationControls
          currentPage={pagination.currentPage || 1}
          totalPages={pagination.totalPages || 1}
          totalItems={pagination.totalItems || 0}
          limit={itemsPerPage}
          hasNextPage={currentPage < (pagination.totalPages || 1)}
          hasPrevPage={currentPage > 1}
          onPageChange={(page) => {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onLimitChange={(newLimit) => {
            setItemsPerPage(newLimit);
            setCurrentPage(1);
          }}
          limitOptions={[10, 25, 50, 100]}
          border={false}
        />
      </div>

      {/* Apply WFH Modal */}
      <WfhApplyModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        onSubmit={(payload) => applyMutation.mutate(payload)}
        isAssign={false}
        submitting={applySubmitting}
      />

      {/* Details Modal */}
      <WfhDetailsModal
        isOpen={showDetails}
        onClose={closeDetails}
        request={selectedRequest}
        formatDate={formatDate}
        getEmployeeName={getEmployeeName}
      />
    </div>
  );
};

export default MyWFHRequests;
